/**
 * Bridge Club Member Detail
 *
 * Detailed analytics for a single member showing:
 * - Member profile and loyalty status
 * - Laura AI analysis and value tier
 * - Purchase history
 * - Animals and feed preferences
 * - Engagement suggestions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import LauraInsightsPanel from '../../components/Admin/LauraInsightsPanel';
import ConversationTimeline from '../../components/ConversationTimeline';
import { customerCRMAPI, CustomerInteraction } from '../../services/customerCrmAPI';
import '../../components/Admin/LauraInsightsPanel.css';
import './BridgeClubPages.css';
import './BridgeClubMemberDetail.css';

interface LinkedStore {
  id: number;
  name: string;
}

interface Member {
  id: number;
  member_number: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  // Resolved server-side: prefers the delivery service address over
  // city/state, whose state defaults to 'WA'. null when genuinely unknown.
  location: string | null;
  joined_at: string | null;
  last_login: string | null;
  linked_store: LinkedStore | null;
  // 'partner' = legacy retail-partner Customer; 'mill' = home_location
  // RetailLocation fallback (e.g. local members homed at the mill).
  linked_store_type: 'partner' | 'mill' | null;
}

interface Loyalty {
  punches: number;
  total_redemptions: number;
  lifetime_savings: number;
  pending_redemptions: number;
}

interface PurchaseStats {
  total_purchases: number;
  total_spent: number;
  avg_order: number;
  days_since_last: number | null;
  last_purchase_date: string | null;
}

// New: POS + online merged stats from the admin_member_detail combined block.
interface CombinedStats {
  total_purchases: number;
  total_spent: number;
  pos_count: number;
  pos_total: number;
  online_count: number;
  online_total: number;
  last_purchase_date: string | null;
  days_since_last: number | null;
}

interface Animal {
  id: number;
  name: string;
  type: string;
  quantity: number;
  purpose: string;
}

interface Product {
  name: string;
  quantity: number;
  spent: number;
}

interface PurchaseItem {
  name: string;
  qty: number;
}

interface Purchase {
  id: number;
  date: string;
  store: string;
  total: number;
  rb_revenue: number;
  items: PurchaseItem[];
}

// Combined recent-purchases row — POS or online, with line items inline.
interface CombinedRecentItem {
  product_name: string;
  quantity: number;
  unit_price: number;
  line_total: number;
  is_red_bridge?: boolean;
}

interface CombinedRecentPurchase {
  source: 'pos' | 'online';
  source_label: string;
  id: number;
  invoice_number: string | null;
  transaction_number: string | null;
  date: string | null;
  total: number;
  rb_revenue?: number;
  cashier?: string | null;
  items: CombinedRecentItem[];
}

interface ConversionBanner {
  converted_at: string;
  transactions_migrated: number;
  retroactive_spend: number;
  retroactive_points: number;
  rewards_available: number;
  credit_value_dollars: number;
  points_to_next_reward: number;
  days_since_conversion: number;
}

interface LauraAnalysis {
  member_profile: string;
  value_tier: 'champion' | 'loyal' | 'growing' | 'at_risk' | 'dormant';
  purchase_patterns: Array<{ pattern: string; implication: string }>;
  engagement_suggestions: Array<{ suggestion: string; timing: string; expected_result: string }>;
  ai_generated: boolean;
}

// --- Delivery subscription (delivery/member_summary.py) ---------------------
interface OnboardingStep {
  key: string;
  label: string;
  complete: boolean;
}

interface DeliveryAnimal {
  id: number;
  name: string;
  animal_type: string;
  category: string;
  count: number;
  weight_lbs: number | null;
  life_stage: string;
  breed: string;
}

interface DeliveryBinProduct {
  id: number;
  brand: string;
  name: string;
  source: string;
}

interface DeliveryBinStoreProduct {
  id: number;
  name: string;
  sku: string;
  size_lbs: string | null;
  tv_retail_price: string | null;
}

interface DeliveryBin {
  id: number;
  name: string;
  feed_category: string;
  form_type: string;
  current_lbs: number | null;
  feed_product: DeliveryBinProduct | null;
  deliverable: boolean;
  not_deliverable_reason: string | null;
  store_product: DeliveryBinStoreProduct | null;
}

interface DeliveryStandingOrderItem {
  id: number;
  bag_count: number;
  unit_price: string;
  is_active: boolean;
  seasonal: boolean;
  product_name: string | null;
  seasonal_form: string;
}

interface DeliveryStandingCatalogItem {
  id: number;
  product_name: string;
  brand: string;
  bag_count: number;
  schedule_mode: string;
  interval_deliveries: number;
  last_delivered_ordinal: number | null;
  is_active: boolean;
}

interface DeliverySummary {
  subscriber_id: number;
  status: string;
  status_label: string;
  service_tier: string | null;
  service_tier_label: string | null;
  preferred_day: string | null;
  preferred_day_label: string | null;
  storage_capacity_bags: number | null;
  address: {
    service_address: string;
    city: string;
    state: string;
    postal_code: string;
    latitude: string | null;
    longitude: string | null;
  };
  onboarding: {
    steps: OnboardingStep[];
    next_step: string | null;
    complete: boolean;
    completed_at: string | null;
  };
  animals: DeliveryAnimal[];
  bins: DeliveryBin[];
  standing_order_items: DeliveryStandingOrderItem[];
  standing_catalog_items: DeliveryStandingCatalogItem[];
  access: {
    captured: boolean;
    captured_at?: string | null;
    skipped: boolean;
    photo_count: number;
    surveyed: boolean;
  };
  payment_method: {
    on_file: boolean;
    last_four: string | null;
    brand: string | null;
    is_active?: boolean;
  };
  lead_id: number | null;
}

interface MemberData {
  member: Member;
  loyalty: Loyalty;
  purchase_stats: PurchaseStats;
  combined_stats?: CombinedStats;
  animals: Animal[];
  favorite_products: Product[];
  combined_favorite_products?: Product[];
  recent_purchases: Purchase[];
  recent_combined?: CombinedRecentPurchase[];
  laura_analysis: LauraAnalysis;
  conversion_banner?: ConversionBanner | null;
  // null when this member is not a delivery subscriber - the whole section hides.
  delivery?: DeliverySummary | null;
}

const BridgeClubMemberDetail: React.FC = () => {
  const navigate = useNavigate();
  const { memberId } = useParams<{ memberId: string }>();
  const [data, setData] = useState<MemberData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Tracks which recent-purchase rows are expanded to show line items.
  // Key shape: `${source}-${id}` to avoid POS/online id collisions.
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [interactions, setInteractions] = useState<CustomerInteraction[]>([]);
  const [interactionsLoading, setInteractionsLoading] = useState(true);
  const [interactionsError, setInteractionsError] = useState<string | null>(null);

  const toggleRowExpansion = (key: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const fetchData = useCallback(async () => {
    if (!memberId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<MemberData>(`/admin/bridge-club/members/${memberId}/detail/`);
      setData(response.data);
    } catch (err: any) {
      console.error('Failed to load member detail:', err);
      setError(err.response?.data?.error || 'Failed to load member details');
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  // Conversations are fetched separately from the member detail: that endpoint runs
  // a Laura AI analysis pass, and a timeline read should not wait on it (or be lost
  // if it fails). Failure here is non-fatal - the rest of the page still renders.
  const fetchInteractions = useCallback(async () => {
    if (!memberId) return;
    setInteractionsLoading(true);
    setInteractionsError(null);
    try {
      const response = await customerCRMAPI.getBCMemberInteractions(Number(memberId));
      setInteractions(response.data);
    } catch (err: any) {
      console.error('Failed to load member interactions:', err);
      setInteractionsError('Could not load conversation history');
    } finally {
      setInteractionsLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchInteractions();
  }, [fetchInteractions]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getTierConfig = (tier: string) => {
    switch (tier) {
      case 'champion': return { label: 'Champion', color: '#059669', bg: '#d1fae5' };
      case 'loyal': return { label: 'Loyal', color: '#3b82f6', bg: '#dbeafe' };
      case 'growing': return { label: 'Growing', color: '#8b5cf6', bg: '#ede9fe' };
      case 'at_risk': return { label: 'At Risk', color: '#f59e0b', bg: '#fef3c7' };
      case 'dormant': return { label: 'Dormant', color: '#6b7280', bg: '#f3f4f6' };
      default: return { label: tier, color: '#6b7280', bg: '#f3f4f6' };
    }
  };

  // Delivery status badge palette. Every pair measured against its own background:
  // active 6.8:1, onboarding 6.4:1, paused 8.1:1, cancelled 6.8:1, waitlist 6.9:1
  // - all clear WCAG AA for body text (4.5:1).
  const getDeliveryStatusConfig = (status: string) => {
    switch (status) {
      case 'active': return { label: 'Delivery Active', color: '#065F46', bg: '#d1fae5' };
      case 'onboarding': return { label: 'Delivery Onboarding', color: '#92400E', bg: '#FEF3C7' };
      case 'paused': return { label: 'Delivery Paused', color: '#3730A3', bg: '#E0E7FF' };
      case 'cancelled': return { label: 'Delivery Cancelled', color: '#991B1B', bg: '#FEE2E2' };
      case 'waitlist': return { label: 'Delivery Waitlist', color: '#4b5563', bg: '#f3f4f6' };
      default: return { label: `Delivery ${status}`, color: '#4b5563', bg: '#f3f4f6' };
    }
  };

  const getAnimalIcon = (type: string) => {
    const t = type.toLowerCase();
    if (t.includes('chicken') || t.includes('hen') || t.includes('layer') || t.includes('broiler') || t.includes('poultry')) return 'C';
    if (t.includes('goat')) return 'G';
    if (t.includes('pig') || t.includes('swine')) return 'P';
    if (t.includes('cow') || t.includes('cattle') || t.includes('beef')) return 'B';
    if (t.includes('sheep') || t.includes('lamb')) return 'S';
    if (t.includes('rabbit')) return 'R';
    if (t.includes('duck')) return 'D';
    if (t.includes('turkey')) return 'T';
    return 'A';
  };

  if (loading) {
    return (
      <div className="bridge-club-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading member profile...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bridge-club-page">
        <div className="page-header">
          <div className="header-content">
            <button className="back-button" onClick={() => navigate(-1)}>
              &larr; Back
            </button>
            <h1>Member Details</h1>
          </div>
        </div>
        <div className="error-banner">
          {error || 'Failed to load data'}
          <button onClick={fetchData}>Retry</button>
        </div>
      </div>
    );
  }

  const {
    member, loyalty, purchase_stats, combined_stats, animals,
    favorite_products, combined_favorite_products, recent_purchases,
    recent_combined, laura_analysis, conversion_banner, delivery,
  } = data;
  const tierConfig = getTierConfig(laura_analysis.value_tier);
  // Prefer combined stats (POS + online) when the new backend payload is
  // present; fall back to the legacy BridgeClubPurchase-only stats otherwise.
  const lifetimeSpend = combined_stats?.total_spent ?? purchase_stats.total_spent;
  const lifetimeCount = combined_stats?.total_purchases ?? purchase_stats.total_purchases;
  const daysSinceLast = combined_stats?.days_since_last ?? purchase_stats.days_since_last;
  const favoriteList = combined_favorite_products && combined_favorite_products.length > 0
    ? combined_favorite_products
    : favorite_products;
  const recentList: CombinedRecentPurchase[] = recent_combined && recent_combined.length > 0
    ? recent_combined
    : recent_purchases.map((p) => ({
        source: 'online',
        source_label: p.store || 'Bridge Club online',
        id: p.id,
        invoice_number: null,
        transaction_number: null,
        date: p.date,
        total: p.total,
        rb_revenue: p.rb_revenue,
        items: p.items.map((it) => ({
          product_name: it.name,
          quantity: it.qty,
          unit_price: 0,
          line_total: 0,
        })),
      }));

  return (
    <div className="bridge-club-page member-detail">
      <div className="page-header">
        <div className="header-content">
          <button className="back-button" onClick={() => navigate(-1)}>
            &larr; Back
          </button>
          <h1>{member.first_name} {member.last_name}</h1>
          <p>{member.email}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {/* A member who is a delivery subscriber must be obviously one, at the
              top of the page, before any scrolling. */}
          {delivery && (() => {
            const cfg = getDeliveryStatusConfig(delivery.status);
            const sub = [delivery.service_tier_label, delivery.preferred_day_label]
              .filter(Boolean).join(' · ');
            return (
              <div className="delivery-badge" style={{ backgroundColor: cfg.bg, color: cfg.color }}>
                <span>{cfg.label}</span>
                {sub && <span className="sub">{sub}</span>}
              </div>
            );
          })()}
          <div className="member-tier-badge" style={{ backgroundColor: tierConfig.bg, color: tierConfig.color }}>
            {tierConfig.label}
          </div>
        </div>
      </div>

      {/* Member Info Card */}
      <div className="member-info-card">
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Member #</span>
            <span className="info-value code">{member.member_number}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Phone</span>
            <span className="info-value">{member.phone || 'Not provided'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Location</span>
            {/* Was `{city}, {state}`, which rendered a bare ", WA" for any member
                with no city - BridgeClubMember.state defaults to 'WA'. The server
                now resolves this, preferring the delivery service address. */}
            <span className={`info-value${member.location ? '' : ' empty'}`}>
              {member.location || 'Not provided'}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Linked Store</span>
            <span className="info-value">
              {member.linked_store ? (
                <>
                  {member.linked_store_type === 'partner' ? (
                    <button
                      className="store-link"
                      onClick={() => navigate(`/admin/bridge-club/stores/${member.linked_store!.id}`)}
                    >
                      {member.linked_store.name}
                    </button>
                  ) : (
                    <span>{member.linked_store.name}</span>
                  )}
                  {member.linked_store_type && (
                    <span
                      style={{
                        marginLeft: 8, padding: '1px 8px', borderRadius: 10,
                        fontSize: 11, fontWeight: 600,
                        background: member.linked_store_type === 'mill' ? '#ccfbf1' : '#dbeafe',
                        color: member.linked_store_type === 'mill' ? '#0f766e' : '#1e40af',
                      }}
                    >
                      {member.linked_store_type === 'mill' ? 'Mill' : 'Partner'}
                    </span>
                  )}
                </>
              ) : 'None'}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Member Since</span>
            <span className="info-value">{formatDate(member.joined_at)}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Last Login</span>
            <span className="info-value">{formatDate(member.last_login)}</span>
          </div>
        </div>
      </div>

      {/* Conversion welcome banner — active 30 days post walk-in conversion. */}
      {conversion_banner && (
        <div style={{
          background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
          border: '1px solid #F59E0B',
          borderRadius: 12,
          padding: 20,
          marginBottom: 20,
          color: '#78350F',
        }}>
          <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 6 }}>
            ★ Came from a walk-in — {conversion_banner.days_since_conversion} day
            {conversion_banner.days_since_conversion === 1 ? '' : 's'} ago
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.5 }}>
            Inherited <strong>{conversion_banner.transactions_migrated}</strong> past visit
            {conversion_banner.transactions_migrated === 1 ? '' : 's'} totaling{' '}
            <strong>{formatCurrency(conversion_banner.retroactive_spend)}</strong> from their
            UnmatchedVisitor shadow profile. Started with{' '}
            <strong>{conversion_banner.retroactive_points} points</strong>
            {conversion_banner.rewards_available > 0 ? (
              <> — <strong>{formatCurrency(conversion_banner.credit_value_dollars)}</strong> in store credit ready to use.</>
            ) : (
              <> — {conversion_banner.points_to_next_reward} points until first $10 reward.</>
            )}
          </div>
        </div>
      )}

      {/* Laura AI Analysis Panel */}
      <div className="laura-section">
        <LauraInsightsPanel
          analysis={laura_analysis}
          loading={false}
          title="Laura's Member Analysis"
        />
      </div>

      {/* Key Metrics — uses combined POS+online totals when available so a
          local member's till spend is visible alongside online orders. */}
      <div className="stats-row">
        <div className="stat-card highlight">
          <div className="stat-value">{formatCurrency(lifetimeSpend)}</div>
          <div className="stat-label">Lifetime Value</div>
          {combined_stats && (combined_stats.pos_total > 0 || combined_stats.online_total > 0) && (
            <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
              {formatCurrency(combined_stats.pos_total)} till / {formatCurrency(combined_stats.online_total)} online
            </div>
          )}
        </div>
        <div className="stat-card">
          <div className="stat-value">{lifetimeCount}</div>
          <div className="stat-label">Total Purchases</div>
          {combined_stats && (
            <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
              {combined_stats.pos_count} POS / {combined_stats.online_count} online
            </div>
          )}
        </div>
        <div className="stat-card">
          <div className="stat-value">{formatCurrency(purchase_stats.avg_order)}</div>
          <div className="stat-label">Avg Order</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{daysSinceLast ?? 'N/A'}</div>
          <div className="stat-label">Days Since Purchase</div>
        </div>
      </div>

      {/* ================= Delivery Subscription =================
          Rendered only when a DeliverySubscriber exists. Everything here comes
          from the `delivery` block on /admin/bridge-club/members/<id>/detail/
          (built by delivery/member_summary.py) - one fetch, not two.

          Gate codes and dog notes are deliberately ABSENT: they are encrypted
          and manager-gated in the Delivery Portal, and this is the general
          admin. Only the FACT of a property capture is shown here. */}
      {delivery && (
        <div className="delivery-section">
          <h3 className="md-section-title">Delivery Subscription</h3>

          <div className="delivery-grid">

            {/* --- Service --- */}
            <div className="delivery-card">
              <h4>Service</h4>
              <div className="info-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '0.875rem' }}>
                <div className="info-item">
                  <span className="info-label">Status</span>
                  <span className="info-value">{delivery.status_label}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Service Tier</span>
                  <span className={`info-value${delivery.service_tier_label ? '' : ' empty'}`}>
                    {delivery.service_tier_label || 'Not chosen yet'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Preferred Day</span>
                  <span className={`info-value${delivery.preferred_day_label ? '' : ' empty'}`}>
                    {delivery.preferred_day_label || 'Not chosen yet'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Storage Capacity</span>
                  <span className={`info-value${delivery.storage_capacity_bags != null ? '' : ' empty'}`}>
                    {delivery.storage_capacity_bags != null
                      ? `${delivery.storage_capacity_bags} bags` : 'Unknown'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Payment Method</span>
                  <span className={`info-value${delivery.payment_method.on_file ? '' : ' empty'}`}>
                    {delivery.payment_method.on_file
                      ? `${delivery.payment_method.brand || 'Card'} ****${delivery.payment_method.last_four || '????'}`
                      : 'None on file'}
                  </span>
                </div>
                {delivery.lead_id != null && (
                  <div className="info-item">
                    <span className="info-label">Original Lead</span>
                    <span className="info-value">
                      <button
                        className="store-link"
                        onClick={() => navigate(`/delivery/leads/${delivery.lead_id}`)}
                      >
                        View in Delivery Portal &rarr;
                      </button>
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* --- Service address --- */}
            <div className="delivery-card">
              <h4>Service Address</h4>
              <div className="info-item" style={{ marginBottom: '0.875rem' }}>
                <span className="info-label">Street</span>
                <span className={`info-value wrap${delivery.address.service_address ? '' : ' empty'}`}>
                  {delivery.address.service_address || 'Not provided'}
                </span>
              </div>
              <div className="info-item" style={{ marginBottom: '0.875rem' }}>
                <span className="info-label">Town / State / ZIP</span>
                <span className="info-value wrap">
                  {[delivery.address.city, delivery.address.state].filter(Boolean).join(', ')}
                  {delivery.address.postal_code ? ` ${delivery.address.postal_code}` : ''}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Coordinates</span>
                <span className={`info-value wrap${delivery.address.latitude ? '' : ' empty'}`}>
                  {delivery.address.latitude && delivery.address.longitude
                    ? `${delivery.address.latitude}, ${delivery.address.longitude}`
                    : 'Not geocoded'}
                </span>
              </div>
            </div>

            {/* --- Onboarding (derived server-side, never recomputed here) --- */}
            <div className="delivery-card">
              <h4>Onboarding</h4>
              <div style={{ marginBottom: '0.75rem' }}>
                {delivery.onboarding.complete ? (
                  <span className="pill ok">
                    Complete{delivery.onboarding.completed_at
                      ? ` - ${formatDate(delivery.onboarding.completed_at)}` : ''}
                  </span>
                ) : (
                  <span className="pill warn">
                    In progress
                    {delivery.onboarding.next_step
                      ? ` - next: ${delivery.onboarding.next_step.replace(/_/g, ' ')}` : ''}
                  </span>
                )}
              </div>
              {delivery.onboarding.steps.map((step) => (
                <div key={step.key} className={`step-row ${step.complete ? 'done' : 'todo'}`}>
                  <span className={`step-mark ${step.complete ? 'done' : 'todo'}`}>
                    {step.complete ? '✓' : ''}
                  </span>
                  <span>{step.label}</span>
                </div>
              ))}
            </div>

            {/* --- Property access: the FACT only, never the content --- */}
            <div className="delivery-card">
              <h4>Property Access</h4>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                {delivery.access.captured
                  ? <span className="pill ok">Captured</span>
                  : delivery.access.skipped
                    ? <span className="pill warn">Skipped by customer</span>
                    : <span className="pill off">Not captured</span>}
                {delivery.access.surveyed && <span className="pill ok">Driver surveyed</span>}
              </div>
              <div className="list-line">
                {delivery.access.photo_count} reference photo
                {delivery.access.photo_count === 1 ? '' : 's'}
              </div>
              <div className="muted-note inline" style={{ marginTop: '0.5rem' }}>
                Gate codes and dog notes are encrypted and shown only in the
                Delivery Portal, to delivery managers.
              </div>
            </div>

            {/* --- Animals --- */}
            <div className="delivery-card">
              <h4>Animals ({delivery.animals.length})</h4>
              {delivery.animals.length === 0 ? (
                <div className="muted-note">No animals registered</div>
              ) : delivery.animals.map((a) => (
                <div key={a.id} className="bin-row">
                  <div className="bin-name">
                    {a.name}{a.count > 1 ? ` x${a.count}` : ''}
                  </div>
                  <div className="bin-meta">
                    {[
                      a.animal_type,
                      a.breed || null,
                      a.weight_lbs != null ? `${a.weight_lbs} lb` : null,
                      a.life_stage || null,
                    ].filter(Boolean).join(' - ')}
                  </div>
                </div>
              ))}
            </div>

            {/* --- Feed bins: deliverability is the point of this card --- */}
            <div className="delivery-card">
              <h4>Feed Bins ({delivery.bins.length})</h4>
              {delivery.bins.length === 0 ? (
                <div className="muted-note">No active feed bins</div>
              ) : delivery.bins.map((b) => (
                <div key={b.id} className="bin-row">
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                    <span className="bin-name">{b.name}</span>
                    <span className={`pill ${b.deliverable ? 'ok' : 'warn'}`}>
                      {b.deliverable ? 'Deliverable' : 'Not deliverable'}
                    </span>
                  </div>
                  <div className="bin-meta">
                    {[
                      b.feed_product ? `${b.feed_product.brand} ${b.feed_product.name}` : 'No feed product',
                      b.store_product?.size_lbs ? `${b.store_product.size_lbs} lb bag` : null,
                      b.current_lbs != null ? `${b.current_lbs} lb on hand` : null,
                    ].filter(Boolean).join(' - ')}
                  </div>
                  {!b.deliverable && b.not_deliverable_reason && (
                    <div className="bin-blocked">
                      <strong>Why not:</strong> {b.not_deliverable_reason}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* --- Standing items --- */}
            <div className="delivery-card">
              <h4>Standing Order ({delivery.standing_order_items.length})</h4>
              {delivery.standing_order_items.length === 0 ? (
                <div className="muted-note">No standing order lines</div>
              ) : delivery.standing_order_items.map((it) => (
                <div key={it.id} className="list-line">
                  {it.bag_count}&times; {it.product_name || 'Unnamed'} &mdash; ${it.unit_price}
                  {it.seasonal && <span className="pill off" style={{ marginLeft: 6 }}>Seasonal</span>}
                  {!it.is_active && <span className="pill off" style={{ marginLeft: 6 }}>Inactive</span>}
                </div>
              ))}
            </div>

            <div className="delivery-card">
              <h4>Standing Catalog ({delivery.standing_catalog_items.length})</h4>
              {delivery.standing_catalog_items.length === 0 ? (
                <div className="muted-note">No standing catalog riders</div>
              ) : delivery.standing_catalog_items.map((it) => (
                <div key={it.id} className="list-line">
                  {it.bag_count}&times; {it.brand} {it.product_name}
                  <span className="muted-note" style={{ marginLeft: 6 }}>
                    {it.schedule_mode === 'every_n'
                      ? `every ${it.interval_deliveries} deliveries`
                      : 'next delivery only'}
                  </span>
                  {!it.is_active && <span className="pill off" style={{ marginLeft: 6 }}>Inactive</span>}
                </div>
              ))}
            </div>

          </div>
        </div>
      )}

      {/* Loyalty Status */}
      <div className="loyalty-card">
        <h3>Loyalty Status</h3>
        <div className="loyalty-grid">
          <div className="punch-card">
            <div className="punch-progress">
              <div className="punch-filled" style={{ width: `${loyalty.punches * 10}%` }} />
            </div>
            <div className="punch-label">{loyalty.punches}/10 Punches</div>
          </div>
          <div className="loyalty-stat">
            <span className="loyalty-value">{loyalty.total_redemptions}</span>
            <span className="loyalty-label">Redemptions</span>
          </div>
          <div className="loyalty-stat">
            <span className="loyalty-value">{formatCurrency(loyalty.lifetime_savings)}</span>
            <span className="loyalty-label">Lifetime Savings</span>
          </div>
          <div className="loyalty-stat">
            <span className="loyalty-value">{loyalty.pending_redemptions}</span>
            <span className="loyalty-label">Pending</span>
          </div>
        </div>
      </div>

      {/* Animals and Products */}
      <div className="detail-grid">
        {/* Animals */}
        <div className="data-card">
          <h3>Animals</h3>
          {animals.length === 0 ? (
            <div className="no-data-small">No animals registered</div>
          ) : (
            <div className="animals-list">
              {animals.map((animal) => (
                <div key={animal.id} className="animal-item">
                  <div className="animal-icon">{getAnimalIcon(animal.type)}</div>
                  <div className="animal-info">
                    <span className="animal-name">{animal.name || animal.type}</span>
                    <span className="animal-details">
                      {animal.quantity} {animal.type} • {animal.purpose}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Favorite Products — merged across POS + online when available. */}
        <div className="data-card">
          <h3>Favorite Products</h3>
          {favoriteList.length === 0 ? (
            <div className="no-data-small">No purchase history</div>
          ) : (
            <div className="products-list">
              {favoriteList.map((product, index) => (
                <div key={index} className="product-item">
                  <div className="product-rank">#{index + 1}</div>
                  <div className="product-info">
                    <span className="product-name">{product.name}</span>
                    <span className="product-qty">{product.quantity} purchased</span>
                  </div>
                  <span className="product-spent">{formatCurrency(product.spent)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Conversation History — phone calls matched to this member by CustomerPhone.
          CustomerInteraction is owned by a Customer XOR a BridgeClubMember; every CRM
          read path filtered on customer=, so BC-member calls had no timeline at all
          until this section. Same card the CRM customer page renders. */}
      <div className="purchases-section">
        <h3>Conversation History</h3>
        {interactionsLoading ? (
          <div className="no-data">Loading conversations…</div>
        ) : interactionsError ? (
          <div className="no-data">{interactionsError}</div>
        ) : interactions.length === 0 ? (
          <div className="no-data">No conversations recorded</div>
        ) : (
          <div>
            <ConversationTimeline interactions={interactions} />
          </div>
        )}
      </div>

      {/* Recent Purchases — combined POS + online view, chevron-expandable to
          show line items, invoice numbers link to the POS receipt detail page. */}
      <div className="purchases-section">
        <h3>Recent Purchases</h3>
        {recentList.length === 0 ? (
          <div className="no-data">No purchase history</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {recentList.map((purchase) => {
              const key = `${purchase.source}-${purchase.id}`;
              const isExpanded = expandedRows.has(key);
              const badgeColor = purchase.source === 'pos' ? '#1E40AF' : '#7C3AED';
              const badgeBg = purchase.source === 'pos' ? '#DBEAFE' : '#EDE9FE';
              return (
                <div key={key} style={{
                  border: '1px solid #E5E7EB',
                  borderRadius: 8,
                  background: '#FFFFFF',
                  overflow: 'hidden',
                }}>
                  <div
                    onClick={() => toggleRowExpansion(key)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '10px 12px',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ width: 14, color: '#6B7280', fontSize: 14 }}>
                      {isExpanded ? '▼' : '▶'}
                    </span>
                    <span style={{
                      background: badgeBg,
                      color: badgeColor,
                      borderRadius: 4,
                      padding: '2px 6px',
                      fontSize: 11,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                    }}>
                      {purchase.source}
                    </span>
                    <span style={{ minWidth: 90, color: '#374151', fontSize: 13 }}>
                      {formatDate(purchase.date)}
                    </span>
                    {purchase.invoice_number ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/bridge-club/pos-receipt/${purchase.invoice_number}`);
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#2D5A27',
                          cursor: 'pointer',
                          fontFamily: 'monospace',
                          fontSize: 12,
                          textDecoration: 'underline',
                          padding: 0,
                        }}
                      >
                        {purchase.invoice_number}
                      </button>
                    ) : (
                      <span style={{ color: '#9CA3AF', fontSize: 12 }}>{purchase.source_label}</span>
                    )}
                    {purchase.cashier && (
                      <span style={{ color: '#6B7280', fontSize: 12 }}>· {purchase.cashier}</span>
                    )}
                    <span style={{ marginLeft: 'auto', fontWeight: 600, color: '#166534' }}>
                      {formatCurrency(purchase.total)}
                    </span>
                  </div>
                  {isExpanded && (
                    <div style={{
                      borderTop: '1px solid #E5E7EB',
                      background: '#F9FAFB',
                      padding: '10px 12px 12px 38px',
                    }}>
                      {purchase.items.length === 0 ? (
                        <div style={{ color: '#9CA3AF', fontSize: 13, fontStyle: 'italic' }}>
                          No line items recorded
                        </div>
                      ) : (
                        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                          <tbody>
                            {purchase.items.map((item, idx) => (
                              <tr key={idx} style={{ borderBottom: '1px dashed #E5E7EB' }}>
                                <td style={{ padding: '4px 0', color: '#1F2937' }}>
                                  {item.product_name}
                                  {item.is_red_bridge && (
                                    <span style={{
                                      marginLeft: 6,
                                      fontSize: 10,
                                      color: '#065F46',
                                      background: '#D1FAE5',
                                      borderRadius: 3,
                                      padding: '0 4px',
                                    }}>
                                      RB
                                    </span>
                                  )}
                                </td>
                                <td style={{ padding: '4px 8px', color: '#6B7280', textAlign: 'right' }}>
                                  {item.quantity}
                                </td>
                                <td style={{ padding: '4px 8px', color: '#6B7280', textAlign: 'right' }}>
                                  {item.unit_price > 0 ? `@ ${formatCurrency(item.unit_price)}` : ''}
                                </td>
                                <td style={{ padding: '4px 0', color: '#1F2937', textAlign: 'right', fontWeight: 500 }}>
                                  {item.line_total > 0 ? formatCurrency(item.line_total) : ''}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default BridgeClubMemberDetail;
